import type { FC } from 'react'

import { Skeleton, StyledText } from '@skytech/ui'
import { Image } from '@skytech/ui/Image'
import { useQuery } from '@tanstack/react-query'
import PailOutlineIcon from 'mdi-react/PailOutlineIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LoadingError } from '@/components/LoadingError'
import RouterLink from '@/components/RouterLink'
import type { AssetExtended } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'
import { imagePlaceholder } from '@/util/misc'

import queries from '../../queries'

export const InfoCard: FC<{
  userTypeId: number | null
}> = ({ userTypeId }) => {
  const translate = useTranslation()
  const params = useParams()
  const assetId = Number(params.id)
  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))

  const { data: assetDetails, isError: isAssetDetailsError } = useQuery(
    queries.adminAsset(assetId),
    () => adminResources.getAsset(assetId),
    { retry: 0 }
  )
  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)

  if (isAssetDetailsError) {
    return <LoadingError />
  }

  if (!assetDetails) {
    return (
      <div className="info-card">
        <div className="product-image">
          <Image src="" alt="asset" onError={imagePlaceholder} />
        </div>

        <div className="info-card-content">
          <h3>
            <Skeleton width={200} />
          </h3>

          <div className="info-card-data" />
          <Skeleton width={300} />
        </div>
      </div>
    )
  }

  const userType = assetTypes?.userTypes.find(type => type.id === userTypeId)?.type

  return (
    <div className="info-card">
      <div className="product-image">
        <Image src={assetDetails.imageUrl || ''} alt="asset" onError={imagePlaceholder} />
      </div>

      <div className="info-card-content">
        <h3>
          {assetDetails.assetDescription}
          {userType === 'GREENPOOL' && <RecycleIcon className="ml-1 fill-green-500" />}
          {userType === 'POOL' && <PailOutlineIcon className="ml-1 fill-green-500" />}
        </h3>

        <div className="info-card-data">
          <CostObjectLink assetDetails={assetDetails} />

          <div>
            <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
              <Translation id="Company" />
            </StyledText>
            <StyledText block colorMode="black" marginBottom={8}>
              {assetDetails.customerName}
            </StyledText>
          </div>

          {assetDetails.costObjectId && (
            <div>
              <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
                <Translation id="User phone number" />
              </StyledText>
              <StyledText block colorMode="black" marginBottom={8}>
                {assetDetails.costObjectPhoneNumber}
              </StyledText>
            </div>
          )}

          <div>
            <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
              <Translation id="User type" />
            </StyledText>
            <StyledText block colorMode="black" marginBottom={8}>
              <Translation id={translate(`asset_user_type/${assetDetails.userType}`)} />
            </StyledText>
          </div>

          <div>
            <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
              <Translation id="Asset status" />
            </StyledText>

            <StyledText block colorMode="black" marginBottom={8}>
              <Translation id={`asset_status/${assetDetails.status}`} />
            </StyledText>
          </div>

          {assetDetails.policyId && (
            <div>
              <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
                <Translation id="Policy" />
              </StyledText>

              <RouterLink
                to={APP_ROUTES.HARDWARE_ECOM_POLICY.replace(
                  ':ecomPolicyId',
                  String(assetDetails.policyId)
                )}
              >
                {assetDetails.policyName}
              </RouterLink>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const CostObjectLink: FC<{ assetDetails: AssetExtended }> = ({ assetDetails }) => {
  if (!assetDetails.costObjectId) return null
  const isLocation = assetDetails.costObjectType === 'L'

  return (
    <div>
      <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
        {isLocation ? <Translation id="Asset location" /> : <Translation id="Employee" />}
      </StyledText>

      {isLocation ? (
        <RouterLink
          to={APP_ROUTES.HARDWARE_ASSET_LOCATION.replace(
            ':assetLocationId',
            String(assetDetails.costObjectId)
          )}
        >
          {assetDetails.costObjectName}
        </RouterLink>
      ) : (
        <RouterLink
          to={APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(assetDetails.costObjectId))}
        >
          {assetDetails.costObjectName}
        </RouterLink>
      )}
    </div>
  )
}
